import { UrlConfig, USER_FACING_PRODUCT_CATEGORIES } from '@/setup/routing/routing'

const DEFAULT_TITLE = 'Full Ads. Built by AI.\nFrom one sentence.'
const DEFAULT_SUBTITLE =
    'Paste your link. Superscale researches winning ads, generates insights, and produces ready-to launch ads.'

const DEFAULT_GRADIENT_SRC = '/assets/landing-page/onboarding-section/background.webp'

export const FALLBACK_ASSET_SLUG = 'home'

export interface ILandingRouteContentLogo {
    name: string
    src: string
    alt: string
    showName?: boolean
}

export interface ILandingRouteContent {
    title: string
    subtitle: string
    /** Background image for the onboarding hero section. */
    gradientSrc: string
    /**
     * Override the pathname-derived asset folder. Use to reuse another page's carousel assets
     * (e.g. `/meta/shopify-store` reusing `/shopify-store`). Pass a pathname like `/shopify-store`.
     */
    assetSlugOverride?: string
    /** Custom CTA button label. Defaults to "Get started free". */
    ctaLabel?: string
    /** When set, CTA opens this URL instead of the auth flow. */
    ctaHref?: string
    /** Optional secondary CTA displayed next to the primary landing CTA. */
    secondaryCtaLabel?: string
    /** When set, secondary CTA opens this URL. */
    secondaryCtaHref?: string
    /** Logos displayed at the bottom of the onboarding hero section, replacing the default tagline. */
    bottomLogos?: ILandingRouteContentLogo[]
    /** Label shown above the logos. Defaults to "Trusted by". */
    bottomLogoLabel?: string
    /** Plain-text override for the bottom tagline (e.g. "Used by 500+ growth teams"). Ignored if `bottomLogos` is set. */
    bottomTagline?: string
}

// const DEMO_CALL_URL = 'https://cal.com/team/superscale/demo'

const DEFAULT_CONTENT: ILandingRouteContent = {
    title: DEFAULT_TITLE,
    subtitle: DEFAULT_SUBTITLE,
    gradientSrc: DEFAULT_GRADIENT_SRC,
    // secondaryCtaLabel: 'Book demo call',
    // secondaryCtaHref: DEMO_CALL_URL,
}

const ROUTE_CONTENT_MAP: Record<string, ILandingRouteContent> = {
    '/': DEFAULT_CONTENT,
    '/meta/agent': DEFAULT_CONTENT,
    '/meta/lp': {
        title: 'Create winning ads\nwith AI characters',
        subtitle: 'Discover the most powerful platform to create UGC and static ads with AI.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/formats/ai-ugc-ads': {
        title: 'Create winning ads\nwith AI characters',
        subtitle: 'Discover the most powerful platform to create UGC and static ads with AI.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },

    [UrlConfig['/products/[user_facing_product_category]'].getUrl({
        productCategory: USER_FACING_PRODUCT_CATEGORIES.MOBILE_APP,
    })]: {
        title: "Let's start\nmarketing your app",
        subtitle: DEFAULT_SUBTITLE,
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    [UrlConfig['/products/[user_facing_product_category]'].getUrl({
        productCategory: USER_FACING_PRODUCT_CATEGORIES.E_COMMERCE,
    })]: {
        title: "Let's grow\nyour e-com store",
        subtitle: DEFAULT_SUBTITLE,
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    [UrlConfig['/products/[user_facing_product_category]'].getUrl({
        productCategory: USER_FACING_PRODUCT_CATEGORIES.WEBSITE,
    })]: {
        title: "Let's drive\ncustomers to your website",
        subtitle: DEFAULT_SUBTITLE,
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/lovable': {
        title: 'You built it fast.\nScale it faster.',
        subtitle: 'Create launch posts and marketing assets for your Lovable project in minutes.',
        gradientSrc: '/assets/landing-page/onboarding-section/lovable-gradient.svg',
    },
    '/shopify-store': {
        title: "Let's grow\nyour Shopify store",
        subtitle: DEFAULT_SUBTITLE,
        gradientSrc: '/assets/landing-page/onboarding-section/shopify-gradient.svg',
    },
    '/base44': {
        title: 'You built it fast.\nScale it faster.',
        subtitle: 'Create launch posts and marketing assets for your Base44 project in minutes.',
        gradientSrc: '/assets/landing-page/onboarding-section/base44-gradient.svg',
    },
    '/formats/static-ads': {
        title: 'Create winning\nstatic ads with AI',
        subtitle:
            'Paste your link. Superscale AI researches winning scripts, generates creatives, and produces ads ready to launch ads.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/formats/e-com-assets': {
        title: 'Create stunning e-com\nassets for your store',
        subtitle: 'Paste your link. Superscale Agent generates stunning assets just based on your prompts.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/integrations/meta': {
        title: 'Analyse your Meta ads account\nand build winning ads with AI',
        subtitle:
            'Superscale Agent connects to your account, runs deep AI analysis, recommends what to improve and instantly builds new ads.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/integrations/tiktok': {
        title: 'Analyse your TikTok ads account\nand build winning ads with AI',
        subtitle:
            'Superscale Agent connects to your account, runs deep AI analysis, recommends what to improve and instantly builds new ads.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/integrations/google': {
        title: 'Analyse your Google ads account\nand build winning ads with AI',
        subtitle:
            'Superscale Agent connects to your account, runs deep AI analysis, recommends what to improve and instantly builds new ads.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
    },
    '/creative-strategist': {
        title: 'Hire a Superhuman\nCreative Strategist from $199/mo.',
        subtitle:
            "Superscale understands what's working for your competitors & in your ad account, identifies patterns and builds new creatives based on that.",
        gradientSrc: DEFAULT_GRADIENT_SRC,
        ctaLabel: 'Try for free',
        bottomTagline: 'Used by 500+ growth teams',
    },
    '/ad-research': {
        title: 'Your competitors just launched 47 new ads.\nWe already know which ones work.',
        subtitle:
            'Superscale monitors every ad in your niche, separates the winners from the noise, and turns them into insights you can act on today.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
        ctaLabel: 'Try for free',
    },
    '/static-video-ads': {
        title: "Creative that ships,\nnot sits in a folder.",
        subtitle:
            "Superscale learns from what's winning in your account and your market, then builds statics and videos ready to launch - new concepts every week, no briefing marathons.",
        gradientSrc: DEFAULT_GRADIENT_SRC,
        ctaLabel: 'Try for free',
    },
    '/media-buying': {
        title: 'Your budget never sleeps.\nNeither do agents.',
        subtitle:
            'Superscale manages your campaigns 24/7 - cutting losers early, scaling winners fast, and showing you the reasoning behind every move.',
        gradientSrc: DEFAULT_GRADIENT_SRC,
        ctaLabel: 'Try for free',
    },
    '/ad-analysis': {
        title: 'Your ad account is full of answers.\nStop guessing.',
        subtitle:
            "Superscale digs into your data, finds why your best ads win and your worst ones don't, and gives you takeaways in plain language - not another dashboard.",
        gradientSrc: DEFAULT_GRADIENT_SRC,
        ctaLabel: 'Try for free',
    },
}

/**
 * Resolve landing page content (title, subtitle, gradient, CTA) from the current pathname.
 * Falls back to default content for unknown routes.
 */
export function getLandingRouteContent(pathname: string): ILandingRouteContent {
    return ROUTE_CONTENT_MAP[pathname] ?? DEFAULT_CONTENT
}

/**
 * Home hero copy for the lp-v3 (new) design. Kept separate from ROUTE_CONTENT_MAP['/'] so the OLD
 * home hero keeps its current copy while the new hero shows the redesign's copy. The new hero renders
 * each `\n`-separated line as a stacked span, coloring the LAST line in the orange pixel face.
 */
const V3_HOME_CONTENT: ILandingRouteContent = {
    title: 'Marketing built by AI,\njudged by profit',
    subtitle: 'Superscale is the operating system for marketing teams, freelancers and agencies that scale brands on Meta and Google with AI.',
    gradientSrc: DEFAULT_GRADIENT_SRC,
    ctaLabel: 'Get started',
}

/** Per-route copy for the NEW (lp-v3) hero. Overrides the shared map for routes that need v3-specific copy. */
const V3_ROUTE_CONTENT_OVERRIDES: Record<string, ILandingRouteContent> = {
    '/': V3_HOME_CONTENT,
}

/**
 * Resolve hero content for the NEW (lp-v3) design. Uses a v3-specific override where present (e.g. the
 * home hero copy) and otherwise falls back to the same per-page content the old design uses — so every
 * eligible page's hero just changes its title/subtitle by pathname.
 */
export function getLandingRouteContentV3(pathname: string): ILandingRouteContent {
    return V3_ROUTE_CONTENT_OVERRIDES[pathname] ?? getLandingRouteContent(pathname)
}

/**
 * Resolve the carousel asset slug for a pathname. The slug matches the folder layout under
 * `/public/assets/landing-page/carousel/<slug>/`. Honors `assetSlugOverride` when set so multiple
 * routes can share one folder. Returns `home` for the root and as the universal fallback.
 *
 *   /                         → home
 *   /lovable                  → lovable
 *   /products/mobile-app      → products/mobile-app
 *   /meta/shopify-store + override '/shopify-store' → shopify-store
 */
export function resolveAssetSlug(pathname: string): string {
    const { assetSlugOverride } = getLandingRouteContent(pathname)
    const canonical = assetSlugOverride ?? pathname
    const stripped = canonical.replace(/^\//, '')
    return stripped === '' ? FALLBACK_ASSET_SLUG : stripped
}
